import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Flex,
  Button,
  VStack,
  useColorModeValue,
  Image,
  Spinner,
  HStack,
  Text,
} from "@chakra-ui/react";
import { CustomText } from "@root/components/common/components";
import { AddIcon } from "@chakra-ui/icons";
import { UploadIconNew } from "@root/assets/icons/UploadIconNew";
import AssessmentCard1 from "@root/assets/images/AssessmentCard1.svg";
import AssessmentCard2 from "@root/assets/images/AssessmentCard2.svg";
import {
  CreateAssessmentModal,
  UploadRiskAssessmentModal,
} from "@root/components/modals";
import { RecentAssessmentsTable } from "@root/components/assessments";
import UploadedAssessmentsTable from "@root/components/assessments/UploadedAssessmentsTable";
import { useTranslation } from "react-i18next";
import { useRiskAssessmentContext } from "@root/context/risk-assessment-context";
import {
  PAGINATION_DEFAULTS,
  RISK_ASSESSMENT_POLLING_INTERVAL,
  RISK_ASSESSMENT_STATUS,
  ASSESSMENT_TYPE_TABS,
  ASSESSMENT_TYPE_PARAM,
  ASSESSMENT_PROCESSING_STATUSES,
  ASSESSMENT_ID_QUERY_PARAM,
  ASSESSMENT_TYPE_QUERY_PARAM,
} from "@root/constants/common";
import { RiskAssessmentService } from "@root/services/risk-assessment";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ROUTE_NAMES } from "@root/constants/navigation-routes";
import { transformAssessmentData } from "@root/utils/risk-assessment-helpers";
import type { Assessment } from "@root/@types/risk-assessment";
import { useAssessmentPollingSetup } from "@root/hooks/useAssessmentPollingSetup";

const RiskAssessmentPage: React.FC = () => {
  const { t } = useTranslation("common");
  const [searchParams] = useSearchParams();
  const assessmentType = searchParams.get(ASSESSMENT_TYPE_QUERY_PARAM);
  const assessmentId = searchParams.get(ASSESSMENT_ID_QUERY_PARAM);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<
    ASSESSMENT_TYPE_TABS.CREATED | ASSESSMENT_TYPE_TABS.UPLOADED
  >(ASSESSMENT_TYPE_TABS.CREATED);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [localAssessments, setLocalAssessments] = useState<any[]>([]);
  const pollingRefs = useRef<Record<string, any>>({});
  const {
    fetchRiskAssessments,
    addRiskAssessment,
    updateRiskAssessment,
    deleteRiskAssessment,
    riskAssessmentData,
    riskAssessmentLoading,
    riskAssessmentCount,
    pageNo,
    setPageNo,
  } = useRiskAssessmentContext();
  const navigate = useNavigate();
  const [highlightedAssessmentId, setHighlightedAssessmentId] = useState<
    string | null
  >();
  useEffect(() => {
    const payload = {
      limit: PAGINATION_DEFAULTS.PAGESIZE,
      offset: (pageNo - 1) * PAGINATION_DEFAULTS.PAGESIZE,
      assessmentType:
        selectedTab === ASSESSMENT_TYPE_TABS.CREATED
          ? ASSESSMENT_TYPE_PARAM.CREATED
          : ASSESSMENT_TYPE_PARAM.UPLOADED,
    };
    fetchRiskAssessments(payload);
  }, [pageNo, selectedTab]);

  // Sync local assessments with context data
  useEffect(() => {
    if (riskAssessmentData) {
      setLocalAssessments(riskAssessmentData);
    }
  }, [riskAssessmentData]);

  // Auto-clear row highlight after ~5.5 seconds
  useEffect(() => {
    if (!highlightedAssessmentId) return;
    const timeoutId = setTimeout(() => {
      setHighlightedAssessmentId(null);
    }, RISK_ASSESSMENT_POLLING_INTERVAL);
    return () => clearTimeout(timeoutId);
  }, [highlightedAssessmentId]);

  useEffect(() => {
    if (assessmentId) {
      setHighlightedAssessmentId(assessmentId);
    }
  }, [assessmentId]);

  useEffect(() => {
    if (assessmentType) {
      setSelectedTab(
        Number(assessmentType) === ASSESSMENT_TYPE_PARAM.UPLOADED
          ? ASSESSMENT_TYPE_TABS.UPLOADED
          : ASSESSMENT_TYPE_TABS.CREATED
      );
    }
  }, [assessmentType]);

  const assessments = transformAssessmentData(localAssessments);

  const bgColor = useColorModeValue("white", "gray.800");

  // Polling setup for non-completed items (persists across route changes)
  useAssessmentPollingSetup({
    items: localAssessments,
    setItems: setLocalAssessments,
    processingStatuses: ASSESSMENT_PROCESSING_STATUSES,
    dependencies: [selectedTab],
  });

  const handleCreateAssessment = () => {
    setIsModalOpen(true);
  };

  const handleOpenUpload = () => setIsUploadModalOpen(true);
  const handleCloseUpload = () => setIsUploadModalOpen(false);
  const handleUploadPdf = async (file: File) => {
    // TODO: integrate with backend upload API
    console.log("Uploading Risk Assessment PDF:", file.name, file.size);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleAssessmentSubmit = async (data: AssessmentFormData) => {
    try {
      await addRiskAssessment(data);
      // Modal will close immediately after this succeeds
      // Refetch will happen in onSubmitSuccess callback
    } catch (error) {
      console.error("Error creating assessment:", error);
      throw error; // Re-throw to let modal handle the error state
    }
  };

  const handleStartProcessing = async () => {
    const assessment = assessments[0];
    if (assessment) handleStartAnalysis(assessment);
  };

  const handleStartAnalysis = (assessment: Assessment) => {
    // Clear any existing polling interval for this assessment
    if (pollingRefs?.current[assessment?.id]) {
      clearInterval(pollingRefs?.current[assessment?.id]);
      delete pollingRefs?.current[assessment?.id];
    }

    // First call analyzeRiskAssessment API, then poll getRiskAssessmentById every 5 seconds
    const service = new RiskAssessmentService();

    const startAnalysis = async () => {
      try {
        // First call to start analysis
        await service.analyzeRiskAssessment(Number(assessment?.id));

        // Then start polling getRiskAssessmentById
        const poll = async () => {
          try {
            const res: any = await service.getRiskAssessmentById(
              Number(assessment?.id)
            );
            const item = res?.data?.data;
            if (item) {
              // Update the specific assessment in local state
              setLocalAssessments((prev) =>
                prev.map((assessment) => {
                  return assessment?.id === item?.id ? item : assessment;
                })
              );
              // Stop polling if completed or failed
              if (
                item?.status === RISK_ASSESSMENT_STATUS.COMPLETED ||
                item?.status === RISK_ASSESSMENT_STATUS.EXCEPTION
              ) {
                if (pollingRefs?.current[assessment?.id]) {
                  clearInterval(pollingRefs?.current[assessment?.id]);
                  delete pollingRefs?.current[assessment?.id];
                }
              }
            }
          } catch (e) {
            // stop on error to avoid infinite loop
            if (pollingRefs?.current[assessment?.id]) {
              clearInterval(pollingRefs?.current[assessment?.id]);
              delete pollingRefs?.current[assessment?.id];
            }
          }
        };

        // Start polling every 5 seconds for this assessment
        poll(); // immediate call
        pollingRefs.current[assessment.id] = setInterval(
          poll,
          RISK_ASSESSMENT_POLLING_INTERVAL
        );
      } catch (error) {
        console.error("Error starting analysis:", error);
      }
    };

    startAnalysis();
    console.log("Start analysis for:", assessment);
  };

  // Cleanup all polling intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(pollingRefs?.current)?.forEach((intervalId) => {
        clearInterval(intervalId);
      });
      pollingRefs.current = {};
      // Reset pagination to first page on unmount/navigation away
      setPageNo(1);
    };
  }, []);

  const handleViewOutput = (
    assessment: Assessment | { id: string | number },
    source?: ASSESSMENT_TYPE_TABS.CREATED | ASSESSMENT_TYPE_TABS.UPLOADED
  ) => {
    console.log("View output for:", assessment);
    const assessmentId = String(assessment?.id);
    if (assessmentId) {
      const url = ROUTE_NAMES.RISK_ASSESSMENT_DETAIL.replace(
        ":id",
        assessmentId
      );
      const urlWithSource = source ? `${url}?source=${source}` : url;
      navigate(urlWithSource);
    }
  };

  const handleRetry = (assessment: Assessment) => {
    console.log("Retry for:", assessment);
    // Call the same analysis logic as Start Analysis
    handleStartAnalysis(assessment);
  };

  const handlePageChange = (newPage: number) => {
    setPageNo(newPage);
  };

  const renderContent = () => {
    if (riskAssessmentLoading) {
      return (
        <Flex justify="center" align="center" minH="60vh">
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="#005EB8"
            size="xl"
          />
        </Flex>
      );
    }

    if (assessments.length > 0) {
      return (
        <RecentAssessmentsTable
          assessments={assessments}
          onStartAnalysis={handleStartAnalysis}
          onViewOutput={(assessment) =>
            handleViewOutput(assessment, ASSESSMENT_TYPE_TABS.CREATED)
          }
          onRetry={handleRetry}
          highlightedAssessmentId={highlightedAssessmentId || undefined}
          pagination={true}
          count={riskAssessmentCount}
          pageSize={PAGINATION_DEFAULTS.PAGESIZE}
          page={pageNo}
          onPageChange={handlePageChange}
        />
      );
    } else {
      return (
        <Flex justify="center" align="center" minH="60vh">
          <VStack spacing={8} align="center">
            {/* Assessment Cards Visualization */}
            <Box position="relative">
              {/* Concentric Rings */}
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                w="300px"
                h="300px"
                borderRadius="50%"
                border="1px solid"
                borderColor="gray.200"
                opacity={0.3}
              />
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                w="200px"
                h="200px"
                borderRadius="50%"
                border="1px solid"
                borderColor="gray.200"
                opacity={0.5}
              />
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                w="100px"
                h="100px"
                borderRadius="50%"
                border="1px solid"
                borderColor="gray.200"
                opacity={0.7}
              />

              {/* Assessment Cards */}
              <VStack spacing={4} align="center">
                <Image src={AssessmentCard1} mb={"-25px"} />
                <Image src={AssessmentCard2} />
                <Image src={AssessmentCard1} mt={"-75px"} />
              </VStack>
            </Box>

            {/* Empty State Message */}
            <VStack spacing={4} align="center">
              <CustomText
                fontSize="24px"
                fontWeight="700"
                textColor="#1A202C"
                text={t("assessments.noAssessmentCreated")}
              />
              <CustomText
                fontSize="16px"
                textColor={"#6C757D"}
                text={t("assessments.pleaseCreateAssessment")}
              />
            </VStack>

            {/* Create Assessment Button */}
            <Button
              w={"200px"}
              bg="#005EB8"
              color="white"
              borderRadius="8px"
              p={"16px"}
              fontSize="14px"
              fontWeight="500"
              onClick={handleCreateAssessment}
              _hover={{
                bg: "#005EB8",
              }}
            >
              {t("assessments.createNewAssessment")}{" "}
              <AddIcon boxSize={"12px"} ml={"8px"} />
            </Button>
          </VStack>
        </Flex>
      );
    }
  };

  const renderUploadedContent = () => {
    if (riskAssessmentLoading) {
      return (
        <Flex justify="center" align="center" minH="60vh">
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="#005EB8"
            size="xl"
          />
        </Flex>
      );
    }

    return (
      <UploadedAssessmentsTable
        assessments={assessments as any}
        onStartAnalysis={handleStartAnalysis as any}
        onViewOutput={(assessment) =>
          handleViewOutput(assessment, ASSESSMENT_TYPE_TABS.UPLOADED)
        }
        onRetry={handleRetry as any}
        highlightedAssessmentId={highlightedAssessmentId || undefined}
        pagination={true}
        count={riskAssessmentCount}
        pageSize={PAGINATION_DEFAULTS.PAGESIZE}
        page={pageNo}
        onPageChange={handlePageChange}
        onUpload={handleOpenUpload}
      />
    );
  };

  return (
    <Box bg={bgColor} minH="100vh" p={8}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={8}>
        <VStack align="start" spacing={1}>
          <CustomText
            fontSize="28px"
            fontWeight="700"
            textColor="#1A202C"
            text={t("assessments.recentAssessments")}
          />
          <CustomText
            fontSize="16px"
            textColor={"#5C6670"}
            text={t("assessments.selectAssessmentSubtitle")}
          />
        </VStack>

        <Flex gap={3}>
          <Button
            w={"250px"}
            variant="outline"
            bg="white"
            color="#005EB8"
            borderColor="#005EB8"
            borderRadius="8px"
            p={"16px"}
            fontSize="16px"
            fontWeight="500"
            onClick={handleOpenUpload}
            _hover={{ bg: "#F7F9FC" }}
          >
            {t("assessments.upload.button")} <UploadIconNew ml={"8px"} />
          </Button>
          <Button
            w={"250px"}
            bg="#005EB8"
            color="white"
            borderRadius="8px"
            p={"16px"}
            fontSize="16px"
            fontWeight="500"
            onClick={handleCreateAssessment}
            _hover={{
              bg: "#005EB8",
            }}
          >
            {t("assessments.createNewAssessment")} <AddIcon ml={"8px"} />
          </Button>
        </Flex>
      </Flex>

      {/* Tabs */}
      <Flex justify="flex-end" mb={4}>
        <Flex
          align="center"
          bg="#F2D9C3"
          borderRadius="999px"
          border="1px solid #D8A97D"
          p="4px"
          gap="6px"
        >
          <Box
            as="button"
            px="14px"
            py="8px"
            borderRadius="999px"
            bg={
              selectedTab === ASSESSMENT_TYPE_TABS.CREATED
                ? "white"
                : "transparent"
            }
            color="#1A202C"
            fontSize="14px"
            fontWeight="600"
            border={
              selectedTab === ASSESSMENT_TYPE_TABS.CREATED
                ? "1px solid #E2E8F0"
                : undefined
            }
            onClick={() => {
              setSelectedTab(ASSESSMENT_TYPE_TABS.CREATED);
              setPageNo(1);
            }}
          >
            {t("assessments.tabs.created")}
          </Box>
          <Box
            as="button"
            px="14px"
            py="8px"
            borderRadius="999px"
            bg={
              selectedTab === ASSESSMENT_TYPE_TABS.UPLOADED
                ? "white"
                : "transparent"
            }
            color="#1A202C"
            fontSize="14px"
            fontWeight="600"
            border={
              selectedTab === ASSESSMENT_TYPE_TABS.UPLOADED
                ? "1px solid #E2E8F0"
                : undefined
            }
            onClick={() => {
              setSelectedTab(ASSESSMENT_TYPE_TABS.UPLOADED);
              setPageNo(1);
            }}
          >
            {t("assessments.tabs.uploaded")}
          </Box>
        </Flex>
      </Flex>

      {/* Main Content */}
      {selectedTab === ASSESSMENT_TYPE_TABS.UPLOADED
        ? renderUploadedContent()
        : renderContent()}

      {/* Create Assessment Modal */}
      <CreateAssessmentModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleAssessmentSubmit}
        onSubmitSuccess={() => {
          // Refetch assessments after modal closes
          const payload = {
            limit: PAGINATION_DEFAULTS.PAGESIZE,
            offset: (pageNo - 1) * PAGINATION_DEFAULTS.PAGESIZE,
            assessmentType:
              selectedTab === ASSESSMENT_TYPE_TABS.CREATED
                ? ASSESSMENT_TYPE_PARAM.CREATED
                : ASSESSMENT_TYPE_PARAM.UPLOADED,
          };
          fetchRiskAssessments(payload).catch((error: any) => {
            console.error("Error fetching assessments:", error);
          });
          setPageNo(1);
          setSelectedTab(ASSESSMENT_TYPE_TABS.CREATED);
        }}
      />

      <UploadRiskAssessmentModal
        isOpen={isUploadModalOpen}
        onClose={handleCloseUpload}
        onUpload={handleUploadPdf}
        onUploadSuccess={() => {
          setPageNo(1);
          setSelectedTab(ASSESSMENT_TYPE_TABS.UPLOADED);
        }}
        onStartProcessing={handleStartProcessing}
      />
    </Box>
  );
};

export default RiskAssessmentPage;
